/**
 * OAuth (third-party sign-in) helpers.
 *
 * Flow:
 *   1. Frontend calls POST /auth/oauth/{provider}/start and gets back an
 *      authorize URL + a random state token.
 *   2. We persist the state in sessionStorage so the callback page can
 *      verify it round-tripped, then redirect the browser to the URL.
 *   3. Provider redirects back to /oauth/{provider}/callback?code=…&state=….
 *      The OAuthCallbackPage forwards code + state to the backend.
 *   4. Backend returns a TokenResponse — we fetch /auth/me, populate the
 *      auth store, and navigate to /home (or /onboarding if not complete).
 */
import axios from 'axios'

import api from './client'
import { useAuthStore } from '../stores/authStore'
import type { TokenResponse, User } from '../types'

const STATE_KEY = (provider: string) => `cs-oauth-state-${provider}`

interface OAuthStartResponse {
  authorize_url: string
  state: string
}

/** Begin the OAuth dance — redirect the browser to the provider. */
export async function startOAuth(provider: string): Promise<void> {
  const { data } = await api.post<OAuthStartResponse>(
    `/api/v1/auth/oauth/${provider}/start`,
  )
  // The backend re-checks state on the callback only loosely (no DB round-trip),
  // so we anchor it here too — the callback page compares this value before
  // forwarding the code.
  try { sessionStorage.setItem(STATE_KEY(provider), data.state) } catch { /* ignore */ }
  window.location.assign(data.authorize_url)
}

/** Read + clear the state we stored before redirecting. */
export function consumeStoredOAuthState(provider: string): string | null {
  try {
    const v = sessionStorage.getItem(STATE_KEY(provider))
    sessionStorage.removeItem(STATE_KEY(provider))
    return v
  } catch {
    return null
  }
}

/** Hand the (code, state) returned by the provider to the backend, then sign in. */
export async function completeOAuth(
  provider: string,
  code: string,
  state: string,
): Promise<{ requiresOnboarding: boolean }> {
  const { data: tokens } = await api.post<TokenResponse>(
    `/api/v1/auth/oauth/${provider}/callback`,
    { code, state, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone },
  )
  const { data: user } = await axios.get<User>(
    `${api.defaults.baseURL}/api/v1/auth/me`,
    { headers: { Authorization: `Bearer ${tokens.access_token}` } },
  )
  useAuthStore.getState().login(user, tokens.access_token, tokens.refresh_token)
  return { requiresOnboarding: !user.onboarding_completed }
}

// ── Settings: link / unlink ──────────────────────────────────────────────────

export interface LinkedIdentity {
  provider: string
  email: string | null
  linked_at: string
  last_login_at: string | null
}

export async function listLinkedIdentities(): Promise<LinkedIdentity[]> {
  const { data } = await api.get<{ identities: LinkedIdentity[] }>(
    '/api/v1/auth/oauth/identities',
  )
  return data.identities
}

export async function unlinkProvider(provider: string): Promise<void> {
  await api.delete(`/api/v1/auth/oauth/${provider}`)
}
